/**
 * Live preview of the draft in the real widget bundle (see preview/main.ts
 * for how the frame serves the draft as the widget's config).
 *
 * Three controls sit on top of the frame:
 *   - Screen: Start / Result / Contact form / Sent. The frame walks the real
 *     widget there with canned, local-only answers.
 *   - Device: desktop or phone. "Phone" is a 390 × 740 viewport (scaled to
 *     fit the column), so the widget's phone rules apply for real: a card no
 *     taller than the screen, a live call full screen. "Desktop" is as tall as
 *     the card's fixed height needs, so a Tall card is not clipped.
 *   - Visitor: light or dark mode. With the theme on "Follow visitor" the
 *     widget itself switches (the frame is served the theme a visitor in that
 *     mode would get); a forced theme keeps the widget as it is and only the
 *     page behind it changes, which is exactly what a visitor would see.
 *   - A skeleton until the widget has drawn its first screen.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { defaultRouteCtaLabel } from '../../../diagnosis-widget/src/defaultCopy';
import { useSession } from '../auth/session';
import { Segmented } from '../ui/kit';
import type { PreviewMessage, PreviewScreen } from '../preview/protocol';
import { useDraft } from './draft';
import { toWidgetResponse, type WidgetConfigModel } from './model';

const SCREEN_LABEL: Record<PreviewScreen, string> = {
  start: 'Start',
  result: 'Result',
  // "Form", not "Contact form": five screens fit one row of the preview column.
  contact: 'Form',
  sent: 'Sent',
  emergency: 'Emergency',
};

const SCREEN_OPENING: Record<PreviewScreen, string> = {
  start: 'Loading the start screen',
  result: 'Opening an example result',
  contact: 'Opening the contact form',
  sent: 'Opening the “Sent” screen',
  emergency: 'Opening the emergency screen',
};

/**
 * The label of the result-screen button that opens the contact form, or null
 * when this draft gives visitors no contact form (the app, a call, a booking
 * link or nothing at all).
 */
export function contactFormCtaLabel(config: WidgetConfigModel, partner: string | null): string | null {
  const { routing } = config;
  if (routing.actions.length > 0) {
    const form = routing.actions.find((a) => a.mode === 'partner_dispatch' || (a.mode === 'partner_booking' && !a.url));
    return form ? form.label : null;
  }
  if (routing.mode !== 'partner_dispatch' && routing.mode !== 'partner_booking') return null;
  return config.copy.cta_route ?? defaultRouteCtaLabel('form', partner);
}

/** What a visitor in `visitor` mode sees for this draft's theme. */
export function themeForVisitor(theme: 'auto' | 'light' | 'dark', visitor: 'light' | 'dark'): 'light' | 'dark' {
  return theme === 'auto' ? visitor : theme;
}

/** The phone the preview stands in for: an iPhone-size viewport minus browser chrome. */
export const PHONE_VIEWPORT = { width: 390, height: 740 } as const;

/**
 * The desktop frame's height: 560 px, or enough for a fixed or capped card
 * plus the page gutter (the widget never lets a card outgrow its viewport,
 * so a shorter frame would show a shorter card than visitors get).
 */
export function desktopFrameHeight(size: { min_height_px: number | null; max_height_px: number | null }): number {
  const card = Math.max(size.min_height_px ?? 0, size.max_height_px ?? 0);
  return Math.max(560, card + 64);
}

export function WidgetPreview({ siteKey, frameHeight }: { siteKey: string; frameHeight?: number | string }) {
  const { org } = useSession();
  const { edited, draftMeta } = useDraft();
  const frame = useRef<HTMLIFrameElement>(null);
  const nonce = useRef(0);
  const [ready, setReady] = useState(false);
  const [visitor, setVisitor] = useState<'light' | 'dark'>('light');
  const [device, setDevice] = useState<'desktop' | 'phone'>('desktop');
  const stage = useRef<HTMLDivElement>(null);
  const [stageWidth, setStageWidth] = useState<number>(PHONE_VIEWPORT.width);
  useLayoutEffect(() => {
    const el = stage.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setStageWidth(el.clientWidth || PHONE_VIEWPORT.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [screen, setScreen] = useState<PreviewScreen>('start');
  // The nonce of the last message the frame answered, and how.
  const [rendered, setRendered] = useState<{ nonce: number; screen: PreviewScreen; ok: boolean } | null>(null);
  const [sent, setSent] = useState(0);

  const partner = org?.display_name ?? null;
  const formCtaLabel = contactFormCtaLabel(edited, partner);
  const needsForm = screen === 'contact' || screen === 'sent';
  // The emergency screen shows only while the bypass is on; otherwise visitors see the result.
  const noEmergency = screen === 'emergency' && !edited.safety.emergency_bypass;
  const target: PreviewScreen = (needsForm && !formCtaLabel) || noEmergency ? 'result' : screen;
  const theme = edited.branding.theme;

  // A layout effect, not a passive one: the frame posts `fx-preview-ready`
  // exactly once, and a passive effect can run after the committed <iframe>
  // has started loading (React defers it under load), so the one "ready" could
  // arrive with nobody listening and the preview would stay on its skeleton.
  // A layout effect is attached in the same commit that inserts the frame.
  useLayoutEffect(() => {
    const onMessage = (event: MessageEvent<PreviewMessage>) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      if (event.data?.type === 'fx-preview-ready') setReady(true);
      if (event.data?.type === 'fx-preview-rendered') {
        const { nonce: n, screen: s, ok } = event.data;
        // The current message's answer — or, before anything has drawn, any
        // answer: an edit made while the frame drew its first screen must not
        // leave the skeleton (and "Loading the preview") up.
        if (n === nonce.current) setRendered({ nonce: n, screen: s, ok });
        else setRendered((prev) => prev ?? { nonce: n, screen: s, ok });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!ready) return;
    const timer = window.setTimeout(() => {
      const config = toWidgetResponse(edited, {
        siteKey,
        version: draftMeta?.version ?? 0,
        partner: org ? { id: org.id, display_name: org.display_name, type: org.partner_type } : null,
      });
      config.branding = { ...config.branding, theme: themeForVisitor(edited.branding.theme, visitor) };
      nonce.current += 1;
      setSent(nonce.current);
      frame.current?.contentWindow?.postMessage(
        { type: 'fx-preview-config', config, siteKey, page: visitor, screen: target, formCtaLabel, nonce: nonce.current } satisfies PreviewMessage,
        window.location.origin,
      );
    }, 350);
    return () => window.clearTimeout(timer);
  }, [ready, edited, siteKey, visitor, target, formCtaLabel, org, draftMeta?.version]);

  // Phone: a real 390 px viewport, scaled down to the column when narrower.
  const scale = Math.min(1, stageWidth / PHONE_VIEWPORT.width);
  const frameStyle =
    device === 'phone'
      ? { width: PHONE_VIEWPORT.width, height: PHONE_VIEWPORT.height, transform: scale < 1 ? `scale(${scale})` : undefined, transformOrigin: '0 0' }
      : { height: frameHeight ?? desktopFrameHeight(edited.placement.size) };
  const deviceBox =
    device === 'phone'
      ? { width: Math.round(PHONE_VIEWPORT.width * scale), height: Math.round(PHONE_VIEWPORT.height * scale), margin: '0 auto', overflow: 'hidden' as const }
      : undefined;

  const firstPaint = rendered === null;
  // Only a screen change is announced: an edit re-renders the same screen.
  const busy = !firstPaint && rendered.screen !== target;
  const failed = !firstPaint && rendered.nonce === sent && !rendered.ok;

  return (
    <div className="stack-sm widget-preview">
      <div>
        <div className="label">Live preview</div>
        <p className="small muted">The real widget, rendering this draft. Nothing you do in the preview is sent anywhere.</p>
        <p className="small" data-testid="preview-slot">
          Takes <strong>{edited.placement.size.max_height_px ?? 640} px</strong> of height on your page at every step, so your page
          never moves. Switch between Start, Result and Form: the card keeps its size and longer steps scroll inside it.
        </p>
      </div>
      <div className="preview-controls">
        <Segmented
          label="Preview screen"
          value={screen}
          onChange={setScreen}
          options={(Object.keys(SCREEN_LABEL) as PreviewScreen[]).map((s) => ({ value: s, label: SCREEN_LABEL[s] }))}
        />
        <Segmented
          label="Preview on"
          value={device}
          onChange={setDevice}
          options={[
            { value: 'desktop', label: 'Desktop' },
            { value: 'phone', label: 'Phone' },
          ]}
        />
        <Segmented
          label="Preview as a visitor using"
          value={visitor}
          onChange={setVisitor}
          options={[
            { value: 'light', label: 'Light mode' },
            { value: 'dark', label: 'Dark mode' },
          ]}
        />
      </div>
      <p className="small muted" data-testid="preview-note">
        {needsForm && !formCtaLabel
          ? 'Your visitors don’t get a contact form: after the diagnosis they don’t send you their details. Showing the result instead.'
          : noEmergency
            ? 'Emergency bypass is off (Safety), so visitors never see this screen: a dangerous problem gets an ordinary result. Showing the result instead.'
            : screen === 'emergency'
              ? 'What a visitor sees when the diagnosis finds a life-safety risk (gas, fire, active flooding). An example; its buttons don’t dial from here.'
              : theme === 'auto'
            ? `A visitor whose device is in ${visitor} mode sees the widget’s ${visitor} theme.`
            : `Your theme is “Always ${theme}”, so the widget stays ${theme} for every visitor; only the page behind it changes.`}
      </p>
      <div className="preview-stage" aria-busy={firstPaint || busy} ref={stage} data-device={device}>
        <div className="preview-device" style={deviceBox}>
          <iframe
            ref={frame}
            className="preview-frame"
            src="/preview.html"
            title={device === 'phone' ? 'Widget preview on a phone' : 'Widget preview'}
            style={frameStyle}
          />
        </div>
        {firstPaint ? (
          <div className="preview-skeleton" aria-hidden="true">
            <span className="sk sk-title" />
            <span className="sk sk-line" />
            <span className="sk sk-box" />
            <span className="sk sk-btn" />
          </div>
        ) : null}
        {/* One polite live region for the whole life of the preview: it says
            "Loading the preview" until the first draw, then only a screen
            change or a failure, and is empty otherwise. It used to be part of
            the skeleton, and the text could outlive the draw (second audit,
            2026-09-27). */}
        <div className={firstPaint ? 'visually-hidden' : `preview-status${busy ? ' on' : ''}`} role="status" aria-live="polite" data-testid="preview-status">
          {firstPaint ? 'Loading the preview' : busy ? `${SCREEN_OPENING[target]}…` : failed ? 'Couldn’t open that screen in the preview.' : ''}
        </div>
      </div>
    </div>
  );
}
